import merge from 'lodash/merge';
import { isEmpty, keys, mergeDeepLeft, omit } from 'ramda';
import { Editor, Node, Text, Transforms } from 'slate';

import type { UUID } from '../../types/type-helpers';
import type {
  AlineText,
  DocumentElement,
  DrawingFileMap,
  DynamicContentElement,
  GlobalStylesMap,
  PageDimensions,
  PageDimensionsEdit,
  PageFormatEdit,
  ProcessTemplateField,
  StyleSheetEdit,
  Trigger,
} from '../internal';
import {
  AlineTransforms,
  createDefaultDocElement,
  createDefaultParagraph,
  EditingMode,
  indexFieldsByNodeRefId,
  isDynamicElement,
  Queries,
} from '../internal';

export const DocumentTransforms = {
  /**
   * Copy documentJSON.
   * Takes a processFields flag that, when true, returns all input field text to be the text of the field label.
   */
  copyDocumentJSON: (
    editor: Editor,
    options: {
      currentUserId: UUID;
      drawingMap: DrawingFileMap;
      inputFields: ProcessTemplateField[];
      processFields: boolean;
    }
  ): void => {
    const { currentUserId, drawingMap, inputFields, processFields } = options;
    const nodeRefIdToField = indexFieldsByNodeRefId(inputFields);

    AlineTransforms.withoutNormalizing(editor, () => {
      const dynamicElementEntries = [
        ...Editor.nodes(editor, {
          at: [],
          match: isDynamicElement,
          voids: true,
        }),
      ];
      const dynamicElementPathRefs = dynamicElementEntries.map(([, path]) =>
        Editor.pathRef(editor, path)
      );
      const textEntriesWithComments = [
        ...Editor.nodes<AlineText>(editor, {
          at: [],
          match: node =>
            Text.isText(node) && Array.isArray(node.comments) && Boolean(node.comments.length),
        }),
      ];

      try {
        AlineTransforms.updateDrawingFileIds(editor, { drawingMap });

        for (const [, textPath] of textEntriesWithComments) {
          // Strip all comments out since we don't carry comments over in the copied doc, for now...
          Transforms.setNodes(editor, { comments: [] }, { at: textPath });
        }

        if (!processFields) return;

        /**
         * Since AlineTransforms.updateInputField updates *all instances* of an input field in a document *by fieldId*, we keep track
         * of the fieldIds of *input fields* we've updated so we don't double update.
         */
        const alreadyUpdatedInputFieldIds = new Set<string>();
        for (const dynamicElementPathRef of dynamicElementPathRefs) {
          const dynamicElementPath = dynamicElementPathRef.current;
          if (!dynamicElementPath) continue;

          const dynamicElement = Node.getIf(editor, dynamicElementPath);
          if (!dynamicElement || !isDynamicElement(dynamicElement)) continue;

          const { fieldId } = dynamicElement;
          if (alreadyUpdatedInputFieldIds.has(fieldId)) continue;

          switch (dynamicElement.type) {
            case 'dynamic-block': {
              const { triggers = [] } = dynamicElement;
              const initialTriggerIndex = getInitialDynamicContentTriggerIndex({
                dynamicElement,
                field: nodeRefIdToField[fieldId],
              });
              const initializedTriggers = triggers.map(trigger => ({
                ...trigger,
                contentJSON: Queries.getInitializedTriggerJSON({
                  dynamicContentNode: { children: trigger.contentJSON },
                  fields: nodeRefIdToField,
                }),
              }));
              if (
                typeof initialTriggerIndex !== 'number' ||
                !triggers.length ||
                !triggers[initialTriggerIndex]
              ) {
                const textProps = Queries.getFallbackTextProps(editor, {
                  at: dynamicElementPath,
                  editingMode: EditingMode.EDITING,
                });

                AlineTransforms.setDynamicContentProps(editor, {
                  at: dynamicElementPath,
                  props: {
                    initialTriggerIndex: 'not-set',
                    triggerIndex: 'not-set',
                    triggers: initializedTriggers,
                  },
                });
                AlineTransforms.switchDynamicContentChildren(editor, {
                  entry: [dynamicElement, dynamicElementPath],
                  newChildren: [createDefaultParagraph({ textProps })],
                });

                break;
              }

              const selectedTrigger = triggers.at(initialTriggerIndex);
              if (!selectedTrigger) return;

              // Make sure to set triggers: initializedTriggers *before* switchDynamicContentTrigger so we have the "initialized" triggers in place.
              AlineTransforms.setDynamicContentProps(editor, {
                at: dynamicElementPath,
                props: { triggers: initializedTriggers },
              });
              // switchDynamicContentTrigger acts to swap in the right JSON *and* set triggerIndex === initialTriggerIndex.
              AlineTransforms.switchDynamicContentTrigger(editor, {
                // Pass null to skip assignee checks since we're processing the template.
                assigneeId: null,
                authorId: currentUserId,
                dynamicContentPath: dynamicElementPath,
                editingMode: EditingMode.EDITING,
                select: false,
                selectedTrigger,
              });

              break;
            }
            case 'INPUT': {
              alreadyUpdatedInputFieldIds.add(fieldId);

              AlineTransforms.initializeInputField(editor, {
                authorId: currentUserId,
                fields: nodeRefIdToField,
                inputFieldEntry: [dynamicElement, dynamicElementPath],
              });
            }
          }
        }
      } finally {
        for (const dynamicElementPathRef of dynamicElementPathRefs) dynamicElementPathRef.unref();
      }
    });
  },

  setDiffFirstPageHeaderFooter(
    editor: Editor,
    options: {
      authorId: string;
      diffFirstPage: boolean;
      editingMode: EditingMode;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, diffFirstPage, editingMode } = options;

      AlineTransforms.setHeaderFooterProps(editor, {
        authorId,
        editingMode,
        newHeaderFooter: { diffFirstPage },
      });
    });
  },
  /**
   * Set header/footer props on the document, taking into account editing mode.
   */
  setHeaderFooterProps: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newHeaderFooter: Partial<DocumentElement['headerFooter']>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, newHeaderFooter } = options;
      const [document, documentPath] = Queries.getDocument(editor);
      const activeHeaderFooterProps = Queries.activeHeaderOrFooterProps(document);
      const equalKeys = keys(newHeaderFooter).filter(propKey => {
        switch (propKey) {
          case 'diffFirstPage':
            return newHeaderFooter.diffFirstPage === activeHeaderFooterProps.diffFirstPage;
          case 'evenOdd':
            return newHeaderFooter.evenOdd === activeHeaderFooterProps.evenOdd;
        }
      });
      const nonEqualProps = omit(equalKeys, newHeaderFooter);

      if (isEmpty(nonEqualProps)) return;
      if (editingMode === EditingMode.SUGGESTING) {
        const pageFormatEdit: PageFormatEdit = {
          authorId,
          type: 'pageFormat',
          value: { headerFooter: nonEqualProps },
        };

        AlineTransforms.suggestDocumentEdit(editor, { documentEdit: pageFormatEdit, editingMode });

        return;
      }

      const defaultDocElement = createDefaultDocElement();
      const headerFooter = document.headerFooter ?? defaultDocElement.headerFooter;
      const updatedHeaderFooter: DocumentElement['headerFooter'] = mergeDeepLeft(
        newHeaderFooter,
        headerFooter
      );

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: [document, documentPath],
        props: { headerFooter: nonEqualProps },
      });
      Transforms.setNodes<DocumentElement>(
        editor,
        {
          headerFooter: updatedHeaderFooter,
        },
        { at: documentPath }
      );
    });
  },
  updateOrientation: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      orientation: 'landscape' | 'portrait';
    }
  ): Error | undefined => {
    const { authorId, editingMode, orientation } = options;
    const { height, width } = Queries.activePageDimensions(editor, 'in');
    const orientationAlreadyActive =
      (orientation === 'landscape' && width > height) ||
      (orientation === 'portrait' && height > width);
    if (orientationAlreadyActive) return new Error('Orientation already active.');

    return AlineTransforms.updatePageDimensions(editor, {
      authorId,
      editingMode,
      newPageDimensions: { height: width, width: height },
    });
  },
  updatePageDimensions: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newPageDimensions: Partial<PageDimensions>;
    }
  ): Error | undefined => {
    const { authorId, editingMode, newPageDimensions } = options;

    if (!Queries.pageDimensionsValid(editor, newPageDimensions))
      return new Error('Page dimensions invalid.');

    AlineTransforms.withoutNormalizing(editor, () => {
      if (editingMode === EditingMode.SUGGESTING) {
        const pageDimensionsEdit: PageDimensionsEdit = {
          authorId,
          type: 'pageDimensions',
          value: { pageDimensions: newPageDimensions },
        };

        AlineTransforms.suggestDocumentEdit(editor, {
          documentEdit: pageDimensionsEdit,
          editingMode,
        });

        return;
      }

      const [document, documentPath] = Queries.getDocument(editor);
      // Note we don't use Queries.activePageDimensions because in EditingMode.Editing a user is editing normal page dimensions.
      const pageDimensions = Queries.pageDimensions(editor);
      const updatedPageDimensions: PageDimensions = mergeDeepLeft(
        newPageDimensions,
        pageDimensions
      );

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: [document, documentPath],
        props: { pageDimensions: newPageDimensions },
      });
      Transforms.setNodes<DocumentElement>(
        editor,
        {
          pageDimensions: updatedPageDimensions,
        },
        { at: documentPath }
      );
    });
  },
  updateStyleSheet: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newStyles: Partial<GlobalStylesMap>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, newStyles } = options;

      if (editingMode === EditingMode.SUGGESTING) {
        const styleSheetEdit: StyleSheetEdit = {
          authorId,
          type: 'styleSheet',
          value: { styleSheet: newStyles },
        };

        AlineTransforms.suggestDocumentEdit(editor, { documentEdit: styleSheetEdit, editingMode });

        return;
      }

      const [document, documentPath] = Queries.getDocument(editor);
      const styleSheet = Queries.styleSheet(editor);
      const newStyleSheet: GlobalStylesMap = merge({}, styleSheet, newStyles);

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: [document, documentPath],
        props: { styleSheet: newStyles },
      });
      Transforms.setNodes<DocumentElement>(
        editor,
        { styleSheet: newStyleSheet },
        { at: documentPath }
      );
    });
  },
};

function getInitialDynamicContentTriggerIndex({
  dynamicElement,
  field,
}: {
  dynamicElement: DynamicContentElement;
  field?: ProcessTemplateField;
}): number | 'not-set' {
  const { initialTriggerIndex, triggers = [] } = dynamicElement;
  if (typeof initialTriggerIndex === 'number') return initialTriggerIndex;

  const choiceRefIds = [
    getInitialValueChoiceRefId(field),
    field?.defaultAnswer?.choiceRefId ?? null,
  ];

  for (const choiceRefId of choiceRefIds) {
    if (!choiceRefId) continue;

    const triggerIndex = getTriggerIndexForChoiceRef({
      choiceRefId,
      fieldId: dynamicElement.fieldId,
      triggers,
    });
    if (triggerIndex !== -1) return triggerIndex;
  }

  return 'not-set';
}

function getTriggerIndexForChoiceRef({
  choiceRefId,
  fieldId,
  triggers,
}: {
  choiceRefId: UUID;
  fieldId: UUID;
  triggers: Trigger[];
}) {
  return triggers.findIndex(trigger =>
    getTriggerMatchesChoiceRef({
      choiceRefId,
      fieldId,
      trigger,
    })
  );
}

function getTriggerMatchesChoiceRef({
  choiceRefId,
  fieldId,
  trigger,
}: {
  choiceRefId: UUID;
  fieldId: UUID;
  trigger: Trigger;
}) {
  const [condition] = trigger.conditions;
  if (!condition) return false;
  if (trigger.conditions.length !== 1) return false;

  return condition.fieldId === fieldId && condition.answerOptionChoiceId === choiceRefId;
}

function getInitialValueChoiceRefId(field?: ProcessTemplateField): UUID | null {
  if (field?.initialValue?.type !== 'answer-option') return null;

  return field.initialValue.answerOption.choiceRefId;
}
